import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { ThemeContext } from 'styled-components/macro'
import { useMediaLayout } from 'use-media'

import {
  CarFileCardPriceDefinition,
  ExportPriceInfo,
} from 'components/molecules/carfile-card-price-block'
import PriceEvaluationIndicator from 'components/molecules/price-evaluation-indicator'
import { formatRoundedPrice, isPrice } from 'utilities/format'
import { calculateMargin, hasPriceEvaluation } from 'utilities/price-evaluation'
import { media } from 'utilities/styled'

const Header = styled.div`
  display: flex;
  align-items: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizings.lvl2};
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
`

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizeByFactor(0.5)};
  min-width: 0;
`

const Label = styled.span`
  font-size: ${({ theme }) => theme.baseFontSize};
  font-weight: ${({ theme }) => theme.baseFontWeight};
  line-height: ${({ theme }) => theme.baseLineHeight};
`

const Price = styled.span`
  font-size: ${({ theme }) => theme.sizeByFactor(3.25)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.sizings.lvl4};
  white-space: nowrap;
`

const Aside = styled.div`
  flex-shrink: 0;
  ${({ theme, $alignWithPrice }) =>
    $alignWithPrice && `margin-top: ${theme.sizeByFactor(2.25)};`}
`

const Breakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizeByFactor(1.5)};
  margin-top: ${({ theme }) => theme.sizeByFactor(1.5)};
  padding: ${({ theme }) => theme.sizeByFactor(1.5)};
  background-color: ${({ theme }) => theme.colors.sectionBackground};
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};

  ${media.tv`
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.sizings.lvl2};
    padding: ${({ theme }) => theme.sizeByFactor(0.75)}
      ${({ theme }) => theme.sizeByFactor(1.5)};
  `}
`

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};

  ${({ $tall, theme }) => $tall && `min-height: ${theme.sizeByFactor(4.375)};`}
`

// Margin and the evaluation line up on the right while the breakdown is stacked
const EndItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};
  min-width: 0;
  margin-left: auto;

  ${media.tv`
    margin-left: 0;
  `}
`

const VerticalDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: ${({ theme }) => theme.sizings.lvl4};
  background-color: ${({ theme }) => theme.colors.brandDelta};

  ${({ $rowOnly }) =>
    $rowOnly &&
    `
      display: none;
    `}

  ${media.tv`
    display: block;
  `}
`

const HorizontalDivider = styled.span`
  display: block;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.brandDelta};

  ${media.tv`
    display: none;
  `}
`

/**
 * Pricing section of the car file summary: the selling price as a large
 * amount, with the purchase price, margin, export price and pricing
 * evaluation below it. The `aside` is shown to the right of the price.
 */
const CarfileSummaryPricing = ({
  sellingPrice,
  purchasePrice,
  exportPrice,
  priceEvaluation,
  exportPriceInclVat,
  aside,
  salesView,
  onPriceEvaluationInfoClick,
  className,
  ...restProps
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const isSingleRow = useMediaLayout({ minWidth: theme.metrics.tv })
  const hasPurchasePrice = isPrice(purchasePrice) && Number(purchasePrice) > 0
  const margin = calculateMargin(sellingPrice, purchasePrice)
  const showExportPrice = isPrice(exportPrice)
  const showEvaluation = hasPriceEvaluation(priceEvaluation)
  const showExportGroup = showExportPrice || showEvaluation

  return (
    <div className={className} {...restProps}>
      <Header $center={salesView}>
        <PriceColumn>
          <Label>{t('sellingPrice')}</Label>
          <Price data-test-e2e="selling-price">
            {isPrice(sellingPrice)
              ? formatRoundedPrice(sellingPrice)
              : t('unknown')}
          </Price>
        </PriceColumn>
        {aside && <Aside $alignWithPrice={!salesView}>{aside}</Aside>}
      </Header>
      {!salesView && (hasPurchasePrice || showExportGroup) && (
        <Breakdown>
          {hasPurchasePrice && (
            <>
              <Group>
                <CarFileCardPriceDefinition
                  label={t('purchasePrice')}
                  value={formatRoundedPrice(purchasePrice)}
                />
                {margin !== null && (
                  <EndItem>
                    <VerticalDivider $rowOnly />
                    <CarFileCardPriceDefinition
                      label={t('margin')}
                      value={formatRoundedPrice(margin)}
                      alignEnd={!isSingleRow}
                    />
                  </EndItem>
                )}
                {showExportGroup && <VerticalDivider $rowOnly />}
              </Group>
              {showExportGroup && <HorizontalDivider />}
            </>
          )}
          {showExportGroup && (
            <Group $tall>
              {showExportPrice && (
                <CarFileCardPriceDefinition
                  label={t('exportPrice')}
                  value={formatRoundedPrice(exportPrice)}
                  info={<ExportPriceInfo inclVat={exportPriceInclVat} />}
                  gap={0.5}
                />
              )}
              {showEvaluation && (
                <EndItem>
                  {showExportPrice && <VerticalDivider $rowOnly />}
                  <PriceEvaluationIndicator
                    evaluation={priceEvaluation}
                    onInfoClick={onPriceEvaluationInfoClick}
                  />
                </EndItem>
              )}
            </Group>
          )}
        </Breakdown>
      )}
    </div>
  )
}

CarfileSummaryPricing.propTypes = {
  sellingPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  purchasePrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exportPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** A PriceEvaluationEnum value or the 1-based step number (1 = very good price) */
  priceEvaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** True when the export price includes VAT (exportprijs), false for exportprijs_exbtw */
  exportPriceInclVat: PropTypes.bool,
  /** Shown to the right of the price, e.g. the stock days or the status button */
  aside: PropTypes.node,
  /** In the sales view only the selling price is shown */
  salesView: PropTypes.bool,
  onPriceEvaluationInfoClick: PropTypes.func,
  className: PropTypes.string,
}

CarfileSummaryPricing.defaultProps = {
  sellingPrice: null,
  purchasePrice: null,
  exportPrice: null,
  priceEvaluation: null,
  exportPriceInclVat: false,
  aside: null,
  salesView: false,
  onPriceEvaluationInfoClick: null,
  className: null,
}

export default CarfileSummaryPricing
